import { finsquid } from '@/integrations/finsquid'
import type {
  BankIDLoginOption,
  IFinsquidBankIDLoginResponse
} from '@/integrations/finsquid/types'
import {
  useBankIDAuthentication,
  type UseBankIDAuthentication
} from '@/integrations/finsquid/utils'
import { useResponsive } from '~/composables/useResponsive'

export type UseQR = {
  bankIdClient: UseBankIDAuthentication
  scanQR: (
    userId: string,
    providerId: number,
    loginOptions: BankIDLoginOption,
    scanQrOptions: {
      onReceiveQR: (qr: string) => void
      onReceiveSID: (sid: string) => void
    }
  ) => Promise<IFinsquidBankIDLoginResponse | null | undefined>
}

export type UseQRProps = {
  onError?: (e: unknown) => any;
  onFail?: (status: IFinsquidBankIDLoginResponse) => any;
}

export const useQR = (props: UseQRProps): UseQR => {
  const responsive = useResponsive()
  const bankIdClient = useBankIDAuthentication({
    handleError: (e) => {
      if (props.onError) {
        props.onError(e);
      } else {
        console.error(e);
      }
    },
    handleFail: (status) => {
      if (props.onFail) {
        props.onFail(status);
      } else {
        console.warn('BankID authentication failed:', status);
      }
    }
  })
  const scanQR = async (
    userId: string,
    providerId: number,
    loginOptions: BankIDLoginOption,
    scanQrOptions: {
      onReceiveQR: (qr: string) => void
      onReceiveSID: (sid: string) => void
    }
  ): Promise<IFinsquidBankIDLoginResponse | null | undefined> => {
    finsquid.clearCache()

    let bankIdOpened = false

    const resp = await bankIdClient.start({
      providerId: providerId,
      req: {
        sameDevice: responsive.isSmall.value,
        userId: userId,
        includeRawData: true,
        loginOption: loginOptions
      },
      callback: async (resp) => {
        if (!resp) return

        const autoStartToken = resp?.autostartToken || (resp as any)?.autoStartToken
        if (!bankIdOpened && autoStartToken && responsive.isSmall.value && typeof window !== 'undefined') {
          bankIdOpened = true
          const a = document.createElement('a')
          a.href = `bankid:///?autostarttoken=${autoStartToken}`
          a.click()
        }

        if (resp.qrCodeImage) {
          scanQrOptions.onReceiveQR(resp.qrCodeImage)
        }
        if (resp.imageChallengeData) {
          scanQrOptions.onReceiveQR(resp.imageChallengeData)
        }
        if (resp.sid) {
          scanQrOptions.onReceiveSID(resp.sid)
        }
        if (resp.status === 'complete') {
          bankIdClient.stop()
          return
        }
        if (resp.status === 'error') {
          if (props.onError) {
            props.onError(resp);
          }
          bankIdClient.stop()
          return
        }
      }
    })

    const autoStartToken = resp?.autostartToken || (resp as any)?.autoStartToken
    if (!bankIdOpened && autoStartToken && responsive.isSmall.value && typeof window !== 'undefined') {
      bankIdOpened = true
      const redirect = encodeURIComponent(window.location.href)
      const a = document.createElement('a')
      a.href = `bankid:///?autostarttoken=${autoStartToken}&redirect=${redirect}`
      a.click()
    }
    return resp
  }

  return {
    scanQR,
    bankIdClient
  }
}
